// ColorRulesTab.js - Rules that map interior/exterior color combinations to categories
import {
  React,
  useEffect,
  useState,
  useMemo,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  DataGrid,
  authFetch,
  authFetchRequest,
  alertC,
  confirmC
} from './PricingCommon';

const ColorRulesTab = () => {
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rulesData, categoriesData] = await Promise.all([
        authFetch('/pricing/color-rules'),
        authFetch('/pricing/color-categories')
      ]);
      setRules(rulesData);
      setCategories(categoriesData);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async (formData) => {
    try {
      let updatedRow;
      if (editData?.id) {
        updatedRow = await authFetchRequest(`/pricing/color-rules/${editData.id}`, formData, 'PUT');
        setRules(prev => prev.map(row => row.id === editData.id ? updatedRow : row));
        if (selectedRow?.id === editData.id) setSelectedRow(updatedRow);
      } else {
        updatedRow = await authFetchRequest('/pricing/color-rules', formData, 'POST');
        setRules(prev => [...prev, updatedRow]);
      }
      setOpenDialog(false);
      setEditData(null);

    } catch (err) {
      console.error('Save failed:', err);
      alertC('Failed to save color rule');
    }
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    const confirmed = await confirmC('Are you sure you want to delete this color rule?');
    if (!confirmed) return;
    try {
      await authFetchRequest(`/pricing/color-rules/${selectedRow.id}`, null, 'DELETE');
      setRules(prev => prev.filter(row => row.id !== selectedRow.id));
      setSelectedRow(null);

    } catch (err) {
      console.error('Delete failed:', err);
      alertC('Failed to delete color rule');
    }
  };

  // Map category IDs to names
  const categoryNameMap = useMemo(() => {
    const map = {};
    categories.forEach(c => { map[c.id] = c.name || c.code; });
    return map;
  }, [categories]);

  const columns = [
    { field: 'id', headerName: 'ID', width: 50 },
    { field: 'name', headerName: 'Rule Name', width: 180 },
    { field: 'interior_pattern', headerName: 'Interior Pattern', width: 130 },
    { field: 'exterior_pattern', headerName: 'Exterior Pattern', width: 130 },
    { field: 'color_category_id', headerName: 'Category', width: 180, renderCell: (params) => categoryNameMap[params.value] || (params.value ? `#${params.value}` : '-') },
    { field: 'priority', headerName: 'Priority', width: 70 },
    { field: 'active', headerName: 'Active', width: 70, renderCell: (params) => params.value ? 'Yes' : 'No' }
  ];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Color Rules</Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" size="small" onClick={() => { setEditData(null); setOpenDialog(true); }}>
          Add Rule
        </Button>
        <Button variant="contained" color="secondary" size="small" onClick={() => { setEditData(selectedRow); setOpenDialog(true); }} disabled={!selectedRow}>
          Edit Rule
        </Button>
        <Button variant="outlined" color="error" size="small" onClick={handleDelete} disabled={!selectedRow}>
          Delete Rule
        </Button>
      </Stack>

      <Box sx={{ mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <span style={{ fontSize: '0.85em', color: '#555' }}>
          Color rules match interior/exterior color selections to determine which price category to use.
          Patterns: exact match ("White"), negation ("!White"), wildcard ("*"). Higher priority rules are checked first.
        </span>
      </Box>

      {loading ? <CircularProgress size={24} /> : (
        <DataGrid
          autoHeight
          rowHeight={30}
          rows={rules}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          onRowClick={(params) => setSelectedRow(params.row)}
          sx={{
            '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#e3f2fd' },
            '& .MuiDataGrid-row': { cursor: 'pointer' }
          }}
          getRowClassName={(params) => params.row.id === selectedRow?.id ? 'Mui-selected' : ''}
        />
      )}

      <ColorRuleDialog
        open={openDialog}
        data={editData}
        categories={categories}
        onClose={() => { setOpenDialog(false); setEditData(null); }}
        onSave={handleSave}
      />
    </Box>
  );
};

const ColorRuleDialog = ({ open, data, categories, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '', description: '', interior_pattern: '*', exterior_pattern: '*',
    color_category_id: '', priority: 0, active: true
  });

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || '',
        description: data.description || '',
        interior_pattern: data.interior_pattern || '*',
        exterior_pattern: data.exterior_pattern || '*',
        color_category_id: data.color_category_id || '',
        priority: data.priority || 0,
        active: data.active ?? true
      });
    } else {
      setFormData({ name: '', description: '', interior_pattern: '*', exterior_pattern: '*', color_category_id: '', priority: 0, active: true });
    }
  }, [data, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{data?.id ? 'Edit Color Rule' : 'Add Color Rule'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Rule Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} fullWidth placeholder="e.g., White Interior & Exterior" />
          <TextField label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} fullWidth multiline rows={2} />

          <Typography variant="subtitle2" sx={{ mt: 1, mb: -1 }}>Pattern Matching</Typography>
          <Box sx={{ p: 1.5, bgcolor: '#f9f9f9', borderRadius: 1, fontSize: '0.8rem', color: '#666' }}>
            <strong>Patterns:</strong> "White" = exact match, "!White" = not white, "*" = any color
          </Box>
          <Stack direction="row" spacing={2}>
            <TextField label="Interior Pattern" value={formData.interior_pattern} onChange={(e) => setFormData({ ...formData, interior_pattern: e.target.value })} fullWidth placeholder="e.g., White, !White, *" />
            <TextField label="Exterior Pattern" value={formData.exterior_pattern} onChange={(e) => setFormData({ ...formData, exterior_pattern: e.target.value })} fullWidth placeholder="e.g., White, !White, *" />
          </Stack>

          <FormControl fullWidth>
            <InputLabel>Color Category</InputLabel>
            <Select value={formData.color_category_id} onChange={(e) => setFormData({ ...formData, color_category_id: e.target.value })} label="Color Category">
              <MenuItem value="">-- Select Category --</MenuItem>
              {categories.map(cat => (
                <MenuItem key={cat.id} value={cat.id}>{cat.name || cat.code}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={2}>
            <TextField label="Priority" type="number" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })} sx={{ width: 120 }} helperText="Higher = first" />
            <FormControlLabel control={<Checkbox checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />} label="Active" />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(formData)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColorRulesTab;
